import axiosServices from '@/common/infrastructure/axios';
import { AxiosError } from 'axios';
import {
  CreateRaptorJobError422,
  CreateRaptorJobError400,
  CreateRaptorJobRequest,
  CreateRaptorJobResponse,
  CreateRaptorJobRestOptions
} from '@/raptorjob/domain/job-create-type';
import { raptorJobRestEndpoints } from './endpoints';
import { postApiV3RaptorJobsCreateJob } from '@/libs/api-rest/generated/raptor-jobs/raptor-jobs';
import type { CreateSingleRaptorJobInput } from '@/libs/api-rest/generated/model';

export const postCreateSingleDayJob = async (
  payload: CreateRaptorJobRequest,
  { useMock }: CreateRaptorJobRestOptions
): Promise<CreateRaptorJobResponse> => {
  const { VITE_API_BASE_URL, VITE_MOCK_API_BASE_URL } = import.meta.env as Record<string, string | undefined>;

  const base = useMock ? VITE_MOCK_API_BASE_URL : VITE_API_BASE_URL;
  if (!base) throw new Error(`Missing ${useMock ? 'VITE_MOCK_API_BASE_URL' : 'VITE_API_BASE_URL'}`);

  const path = useMock ? raptorJobRestEndpoints.mockRaptorJobs : raptorJobRestEndpoints.createSingleDayJob;

  try {
    const { data } = await axiosServices.post<CreateRaptorJobResponse>(`${base}${path}`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
    return data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError<CreateRaptorJobError422 | CreateRaptorJobError400>;
    const status = axiosError?.response?.status;

    // Handle 422 errors (old format)
    if (status === 422 && axiosError.response?.data && 'error' in axiosError.response.data) {
      throw axiosError.response.data;
    }

    // Handle 400 errors (new format with validationErrors)
    if (status === 400 && axiosError.response?.data && 'validationErrors' in axiosError.response.data) {
      throw axiosError.response.data;
    }

    throw error;
  }
};

export const postCreateMultiDayJob = async (
  payload: CreateRaptorJobRequest,
  _options: CreateRaptorJobRestOptions
): Promise<CreateRaptorJobResponse> => {
  try {
    const response = await postApiV3RaptorJobsCreateJob(payload as CreateSingleRaptorJobInput);
    // Transform Orval response to domain response format
    // The Orval function returns RaptorJobDto
    // but we need CreateRaptorJobResponse format
    return {
      job: {
        id: String(response.data.id ?? ''),
        name: response.data.name || '',
        status: response.data.status || response.data.jobStatus?.name || '',
        startDate: response.data.startDate || ''
      },
      created: {
        ticketId: undefined,
        resourceGroups: undefined,
        estimates: undefined
      },
      id: String(response.data.id ?? '')
    } as CreateRaptorJobResponse;
  } catch (error: unknown) {
    const axiosError = error as AxiosError<CreateRaptorJobError422 | CreateRaptorJobError400>;
    const status = axiosError?.response?.status;

    // Handle 422 errors (old format)
    if (status === 422 && axiosError.response?.data && 'error' in axiosError.response.data) {
      throw axiosError.response.data;
    }

    // Handle 400 errors (new format with validationErrors)
    if (status === 400 && axiosError.response?.data && 'validationErrors' in axiosError.response.data) {
      throw axiosError.response.data;
    }

    throw error;
  }
};
